import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import currency from 'currency.js';
import { format, isValid, parseISO } from 'date-fns';

import { IBill } from 'common/src/types/interfaces';

const formatBillDate = (date?: string): string => {
  if (!date) {
    return '';
  }

  const parsedDate = parseISO(date);

  return isValid(parsedDate) ? format(parsedDate, 'P') : '';
};

const getImpactSummaryText = (
  rowCount: number,
  deletedCount: number,
): string => {
  if (deletedCount === 0) {
    return rowCount === 1
      ? 'The bill you selected is an allocation and will not be deleted. The only way to delete it is to delete the allocated bill it was generated from.'
      : 'Some of the bills you selected are allocations and will not be deleted. The only way to delete them is to delete the allocated bills they were generated from.';
  }

  return rowCount === 1
    ? "The bill you selected is an allocated bill. Here's what will happen when you confirm its deletion:"
    : "Some of the bills you selected have been allocated. Here's what will happen when you confirm deletion:";
};

const BillsBulkDeletionImpactTable = ({
  rows,
}: {
  rows: {
    bill: IBill;
    willBeDeleted: boolean;
    note: string;
  }[];
}) => {
  const deletedCount = rows.filter((row) => row.willBeDeleted).length;

  return (
    <Box>
      <Typography sx={{ my: 2 }}>
        {getImpactSummaryText(rows.length, deletedCount)}
      </Typography>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Utility</TableCell>
              <TableCell>Account Number</TableCell>
              <TableCell>Expected Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ bill, willBeDeleted, note }) => (
              <TableRow key={bill.id}>
                <TableCell>{formatBillDate(bill.startDate)}</TableCell>
                <TableCell>{formatBillDate(bill.endDate)}</TableCell>
                <TableCell>
                  {bill.isAllocated
                    ? 'Allocated'
                    : currency(bill.amount).format()}
                </TableCell>
                <TableCell>{bill.account?.utility?.name ?? ''}</TableCell>
                <TableCell>{bill.account?.number ?? ''}</TableCell>
                <TableCell
                  sx={{
                    color: willBeDeleted ? 'inherit' : 'warning.main',
                  }}
                >
                  {note}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BillsBulkDeletionImpactTable;
